import { useEffect, useState } from 'react'
import { useUpdateAudience } from '@/api/hooks/use-audiences'
import { Modal } from '@/components/ui/modal'
import { UnsubFeedbackSection } from '@/pages/audiences/unsub-feedback-section'
import type { Audience } from '@/types/newsletter'

interface AudienceEditModalProps {
  open: boolean
  onClose: () => void
  audience: Audience
}

export function AudienceEditModal({
  open,
  onClose,
  audience,
}: AudienceEditModalProps): React.JSX.Element {
  const [name, setName] = useState(audience.name)
  const [description, setDescription] = useState(audience.description ?? '')
  const [unsubMessage, setUnsubMessage] = useState(audience.unsub_message ?? '')
  const [unsubReasons, setUnsubReasons] = useState<string[]>(audience.unsub_reasons ?? [])
  const [includeOther, setIncludeOther] = useState(false)
  const updateAudience = useUpdateAudience()

  useEffect(() => {
    setName(audience.name)
    setDescription(audience.description ?? '')
    setUnsubMessage(audience.unsub_message ?? '')
    setUnsubReasons(audience.unsub_reasons ?? [])
  }, [audience])

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault()
    if (!name.trim()) return

    const trimmedReasons = unsubReasons.filter((r) => r.trim())
    updateAudience.mutate(
      {
        id: audience.id,
        data: {
          name: name.trim(),
          description: description.trim() || undefined,
          unsub_message: unsubMessage.trim() || null,
          unsub_reasons: trimmedReasons.length > 0 ? trimmedReasons : null,
        },
      },
      { onSuccess: () => onClose() },
    )
  }

  return (
    <Modal open={open} onClose={onClose} title="Edit Audience" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="edit-name" className="block text-sm font-medium text-text-primary">
            Name
          </label>
          <input
            id="edit-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary focus:border-brand-500 focus:outline-none"
          />
        </div>

        <div>
          <label htmlFor="edit-desc" className="block text-sm font-medium text-text-primary">
            Description
          </label>
          <textarea
            id="edit-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="mt-1 w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary focus:border-brand-500 focus:outline-none"
          />
        </div>

        <UnsubFeedbackSection
          unsubMessage={unsubMessage}
          onUnsubMessageChange={setUnsubMessage}
          reasons={unsubReasons}
          onReasonsChange={setUnsubReasons}
          includeOther={includeOther}
          onIncludeOtherChange={setIncludeOther}
          audienceName={name}
        />

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-surface-3 px-4 py-2 text-sm font-medium text-text-primary hover:bg-surface-1"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!name.trim() || updateAudience.isPending}
            className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {updateAudience.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
